import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { View } from 'react-native';
import { useGetEventResidentsQuery, useUpdateResidentRsvpMutation } from '@/store/api/admin/eventsApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import { ROWS_PER_PAGE } from '@/constants/admin';
import { AttendanceQueryParams, AttendanceStats, IEventFilters } from '../types';
import { IEvent, ILeaseCustomer } from '@/types/admin/events';
import { computeAttendanceStats, generateAttendanceCsv, resolveEventAttendanceFlags } from '@/utils/admin/eventsAttendance';
import { downloadCsvFrom2DArray } from '@/utils/admin/csvExport';
import { useDebounce } from '@/hooks/admin/useDebounce';
import { useResponsive } from '@/hooks/common/useResponsive';

export const useEventAttendance = (event: IEvent) => {
  const { showSuccess, showError } = useToast();
  const adminUser = useAdminUser();

  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const triggerRef = useRef<View>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [selectedResident, setSelectedResident] = useState<ILeaseCustomer | null>(null);
  const [residentModalOpen, setResidentModalOpen] = useState(false);

  const { showSplitDetails } = useResponsive();
  const wasSplitRef = useRef(showSplitDetails);
  useEffect(() => {
    // narrow → wide: hide the modal so the resident shows in the right pane
    // wide → narrow: do nothing (don't surprise the user with a popup)
    if (!wasSplitRef.current && showSplitDetails) {
      setResidentModalOpen(false);
    }
    wasSplitRef.current = showSplitDetails;
  }, [showSplitDetails]);

  const [pagingForward, setPagingForward] = useState(false);
  const [isFilterRefetch, setIsFilterRefetch] = useState(false);
  const [isUpdatingRsvp, setIsUpdatingRsvp] = useState(false);
  const [lastRefetchSource, setLastRefetchSource] = useState<'search' | 'filter' | null>(null);
  const [attendeeFilters, setAttendeeFilters] = useState<IEventFilters>({
    hasRsvp: true,
    checkedIn: false,
    notCheckedIn: false,
    notRsvp: false,
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const queryParams = useMemo((): AttendanceQueryParams => {
    const params: AttendanceQueryParams = {
      propertyId: adminUser.selectedPropertyId!,
      eventId: event.id,
      pageNum: currentPage,
    };
    if (debouncedSearchQuery.trim()) params.searchQuery = debouncedSearchQuery.trim();
    if (attendeeFilters.hasRsvp) params.hasRsvp = true;
    if (attendeeFilters.notRsvp) params.notRsvp = true;
    if (attendeeFilters.checkedIn) params.checkedIn = true;
    if (attendeeFilters.notCheckedIn) params.notCheckedIn = true;
    return params;
  }, [adminUser.selectedPropertyId, event.id, currentPage, debouncedSearchQuery, attendeeFilters]);

  const {
    data: attendanceData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetEventResidentsQuery(queryParams, { skip: !adminUser.selectedPropertyId });

  const [updateResidentRsvp] = useUpdateResidentRsvpMutation();

  const attendeeList = attendanceData?.rows || [];
  const totalAttendees = attendanceData?.count || 0;
  const totalPages = Math.ceil(totalAttendees / ROWS_PER_PAGE);

  const stats: AttendanceStats = useMemo(() => {
    const { checkedIn, rsvps } = computeAttendanceStats(attendeeList as any, event.id);
    return { checkedIn, rsvps, total: totalAttendees };
  }, [attendeeList, totalAttendees, event.id]);

  const hasActiveFilters = useMemo(() => Object.values(attendeeFilters).some(Boolean), [attendeeFilters]);

  const selectedResidentFlags = useMemo(() => {
    if (!selectedResident) return { checkedIn: false, hasRsvp: false };
    return resolveEventAttendanceFlags(selectedResident as any, event.id);
  }, [selectedResident, event.id]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      setPagingForward(true);
    }
  }, [currentPage, totalPages]);

  const toggleFilter = useCallback((key: string) => {
    setAttendeeFilters(prev => ({ ...prev, [key]: !prev[key] }));
    setCurrentPage(0);
    setIsFilterRefetch(true);
    setLastRefetchSource('filter');
  }, []);

  useEffect(() => {
    if (!isFetching) {
      setPagingForward(false);
      setIsFilterRefetch(false);
      setLastRefetchSource(null);
    }
  }, [isFetching]);

  const onSearchChange = useCallback((text: string) => {
    setIsFilterRefetch(true);
    setSearchQuery(text);
    setCurrentPage(0);
    setLastRefetchSource('search');
  }, []);

  const handleCheckIn = useCallback(async (resident: ILeaseCustomer, checkedIn: boolean) => {
    try {
      setIsUpdatingRsvp(true);
      await updateResidentRsvp({ eventId: event.id, residentId: resident.id, checkedIn }).unwrap();
      setSelectedResident(prev => prev && prev.id === resident.id ? ({
        ...prev,
        EventResident: {
          ...(prev.EventResident || {} as any),
          checkedIn,
          hasRsvp: prev.EventResident?.hasRsvp ?? true,
        },
      } as any) : prev);
      refetch();
      showSuccess(`${resident.firstName} ${resident.lastName} has been ${checkedIn ? 'checked in' : 'checked out'}`);
    } catch (e) {
      showError('Failed to update check-in status');
    } finally {
      setIsUpdatingRsvp(false);
    }
  }, [event.id, updateResidentRsvp, refetch, showSuccess, showError]);

  const handleExport = useCallback(() => {
    const rows = generateAttendanceCsv(attendeeList as any, event.id);
    const filename = `${event.name}_attendees`;
    downloadCsvFrom2DArray(rows as any, filename);
  }, [attendeeList, event.id, event.name]);

  return {
    attendanceData,
    // data
    attendeeList,
    totalAttendees,
    totalPages,
    stats,
    selectedResident,
    selectedResidentFlags,
    // ui state
    currentPage,
    searchQuery,
    showFilters,
    showDropdown,
    triggerRef,
    anchor,
    isFetching,
    isLoading,
    error,
    pagingForward,
    isFilterRefetch,
    isUpdatingRsvp,
    attendeeFilters,
    hasActiveFilters,
    lastRefetchSource,
    residentModalOpen,
    showSplitDetails,
    // setters / handlers
    setShowFilters,
    setShowDropdown,
    setAnchor,
    setSelectedResident,
    setResidentModalOpen,
    handlePreviousPage,
    handleNextPage,
    toggleFilter,
    onSearchChange,
    handleCheckIn,
    handleExport,
  };
};


